import { useTranslation } from "@humansignal/core";
import { getProviderConfig } from "../providers";
import { ProviderForm } from "../components/provider-form";
import Input from "apps/labelstudio/src/components/Form/Elements/Input/Input";
import { Toggle } from "@humansignal/ui";

interface ProviderDetailsStepProps {
  formData: any;
  errors: Record<string, string>;
  handleProviderFieldChange: (name: string, value: any) => void;
  handleFieldBlur?: (name: string, value: any) => void;
  provider?: string;
  isEditMode?: boolean;
  target?: "import" | "export";
}

export const ProviderDetailsStep = ({
  formData,
  errors,
  handleProviderFieldChange,
  handleFieldBlur,
  provider,
  isEditMode = false,
  target,
}: ProviderDetailsStepProps) => {
  const { t } = useTranslation();
  const providerConfig = getProviderConfig(provider);

  if (!provider || !providerConfig) {
    return <div className="text-red-500">{!provider ? "No provider selected" : `Unknown provider: ${provider}`}</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold">{providerConfig.title}</h2>
        <p className="text-muted-foreground">{providerConfig.description}</p>
      </div>

      {/* Title field - common for all providers */}
      <div className="space-y-2">
        <Input
          name="title"
          value={formData.title ?? ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleProviderFieldChange("title", e.target.value)}
          placeholder={t(
            "libs.app-common.blocks.storageproviderform.steps.provider-details-step.enter_a_descriptive_name_e_g_legal_documents_training_data",
          )}
          validate=""
          skip={false}
          labelProps={{}}
          ghost={false}
          tooltip=""
          tooltipIcon={null}
          required={true}
          label={t("libs.app-common.blocks.storageproviderform.steps.provider-details-step.storage_title")}
          description={t(
            "libs.app-common.blocks.storageproviderform.steps.provider-details-step.this_name_will_help_you_identify_this_connection_in_your_pro",
          )}
          footer={errors.title ? <span className="text-negative-content">{errors.title}</span> : ""}
          className={errors.title ? "border-negative-content" : ""}
        />
      </div>

      <ProviderForm
        provider={providerConfig}
        formData={formData}
        errors={errors}
        onChange={handleProviderFieldChange}
        onBlur={handleFieldBlur}
        isEditMode={isEditMode}
        target={target}
      />

      {/* Export-specific common fields */}
      {target === "export" && (
        <div className="space-y-6">
          <div className="space-y-2">
            <Toggle
              checked={formData.can_delete_objects ?? false}
              onChange={(e) => handleProviderFieldChange("can_delete_objects", e.target.checked)}
              aria-label={t(
                "libs.app-common.blocks.storageproviderform.steps.provider-details-step.can_delete_objects_from_storage",
              )}
              label={t(
                "libs.app-common.blocks.storageproviderform.steps.provider-details-step.can_delete_objects_from_storage",
              )}
              description={t(
                "libs.app-common.blocks.storageproviderform.steps.provider-details-step.if_unchecked_annotations_will_not_be_deleted_from_storage",
              )}
            />
          </div>
        </div>
      )}
    </div>
  );
};
